import { useDisclosure } from '@chakra-ui/react';
import { InvoiceStatusEnum, InvoiceSummaryResp } from '@monetize/types/app';
import {
  FilterTypeOperator,
  UseEntityDataResponseType,
} from '@monetize/types/common';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doPrintInvoiceToPdf } from '../../api/accountsService';
import { handleApiErrorToast } from '../../api/axios';
import { doExportEntity } from '../../api/entityExportService';
import { accountServiceQueryKeys } from '../../api/queryKeysService';
import { useRevRecSettings } from '../../api/revenueService';
import { MDataTableActions } from '../../components/Monetize/DataTable';
import { useTableColumns } from '../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../components/Monetize/DataTableNew/DataTable';
import { ExtraActiveFilter } from '../../components/Monetize/DataTableNew/DataTableActiveFilters';
import { ACTION_COLUMN_KEY } from '../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../components/Monetize/DataTableNew/DataTableTypes';
import { ROUTES } from '../../constants';
import { REVENUE_SCHEDULE_STATUSES } from '../../constants/invoice';
import { useACL } from '../../services/acl/acl';
import { useFlags } from '../../services/launchDarkly';
import { downloadBlobAsFile } from '../../utils/download';
import { getVoidInvoiceDisabledReason } from '../../utils/invoices';
import { navigateOrOpenNewWindow } from '../../utils/misc';
import { getDuplicateInvoiceActionTitle } from '../AccountDetails/OneTimeInvoice/oneTimeInvoice.utils';
import { PayInvoiceModal } from './createPayment/CreatePaymentModal';
import {
  INVOICE_LIST_CATALOG,
  InvoiceListColumnContext,
} from './invoiceList.columns';
import { InvoiceRevenueScheduleModal } from './InvoiceRevenueSchedule/InvoiceRevenueScheduleModal';
import { VoidInvoiceModal } from './VoidInvoiceModal';

interface InvoicesProps extends Omit<
  UseEntityDataResponseType<InvoiceSummaryResp>,
  'loadData'
> {
  filterComponentReset: React.MutableRefObject<any>;
  searchComponentReset?: React.MutableRefObject<any>;
  showAccount?: boolean;
  searchTerm?: string;
  source?: 'accounts' | 'dashboard';
  omitBillGroup?: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  onResetFilter: () => void;
  extraActiveFilters?: ExtraActiveFilter[];
}

export const InvoiceList: FC<InvoicesProps> = ({
  source,
  filters,
  setFilters,
  setPager,
  pager,
  isLoading,
  invoices,
  filterComponentReset,
  showAccount = false,
  searchComponentReset = { current: null },
  searchTerm,
  omitBillGroup = false,
  isError,
  error,
  refetch,
  onResetFilter,
  extraActiveFilters,
}: InvoicesProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const accountId = params.accountId || '';

  const tableRef = useRef<DataTableRef>(null);

  const { data: recRevSettings } = useRevRecSettings();
  const queryClient = useQueryClient();
  const { canDo } = useACL();
  const { enableInvoiceVoid } = useFlags();
  const canUpdateBilling = canDo([['billing', 'update']]);
  const canReadBilling = canDo([['billing', 'read']]);
  const paymentModalState = useDisclosure();
  const revenueScheduleModalState = useDisclosure();
  const voidInvoiceModalState = useDisclosure();

  const [invoice, setInvoice] = useState<InvoiceSummaryResp>();

  useEffect(() => {
    if (
      invoice &&
      !paymentModalState.open &&
      !revenueScheduleModalState.open &&
      !voidInvoiceModalState.open
    ) {
      setInvoice(undefined);
    }
  }, [
    invoice,
    paymentModalState.open,
    revenueScheduleModalState.open,
    voidInvoiceModalState.open,
  ]);

  const actionBodyTemplate = useCallback(
    (rowData: InvoiceSummaryResp) => {
      const { status, amount } = rowData;
      const actions = [
        {
          title: 'Pay Invoice',
          enabled:
            status === InvoiceStatusEnum.UNPAID &&
            Number(amount) > 0 &&
            canUpdateBilling,
          action: () => {
            setInvoice(rowData);
            paymentModalState.onOpen();
          },
        },
        {
          title: 'Print to PDF',
          enabled: true,
          action: async () => {
            try {
              const { data, fileName } = await doPrintInvoiceToPdf(
                rowData.id,
                rowData.invoiceNumber,
              );
              downloadBlobAsFile(data, fileName);
            } catch (err) {
              handleApiErrorToast(err);
            }
          },
        },
        {
          title: 'Export CSV',
          enabled: canReadBilling,
          action: async () => {
            try {
              await doExportEntity({
                entity: 'invoice',
                filename: `invoice-${rowData.invoiceNumber || rowData.id}.csv`,
                endpointParams: [rowData.id],
              });
            } catch (err) {
              handleApiErrorToast(err);
            }
          },
        },
        {
          title: 'View Revenue Schedule',
          enabled:
            recRevSettings?.enabled &&
            REVENUE_SCHEDULE_STATUSES.has(rowData.status),
          action: () => {
            setInvoice(rowData);
            revenueScheduleModalState.onOpen();
          },
        },
        {
          title: getDuplicateInvoiceActionTitle(rowData.invoiceType),
          enabled: canUpdateBilling,
          action: () =>
            navigate(
              ROUTES.getOneTimeInvoiceCreateRoute(rowData.account.id, {
                sourceInvoiceId: rowData.id,
              }),
            ),
        },
        {
          title: 'Void Invoice',
          enabled:
            enableInvoiceVoid &&
            canUpdateBilling &&
            !getVoidInvoiceDisabledReason(rowData),
          action: () => {
            setInvoice(rowData);
            voidInvoiceModalState.onOpen();
          },
        },
      ];
      return <MDataTableActions actions={actions} />;
    },
    [
      canUpdateBilling,
      canReadBilling,
      enableInvoiceVoid,
      recRevSettings,
      paymentModalState,
      revenueScheduleModalState,
      voidInvoiceModalState,
      navigate,
    ],
  );

  const columnContext = useMemo<InvoiceListColumnContext>(
    () => ({ actionBodyTemplate, accountId, showAccount, omitBillGroup }),
    [actionBodyTemplate, accountId, showAccount, omitBillGroup],
  );
  const { columns, state: columnState } = useTableColumns(
    INVOICE_LIST_CATALOG,
    columnContext,
  );

  const handlePaymentModalClose = (reload: boolean) => {
    if (reload) {
      queryClient.invalidateQueries({
        queryKey: [accountServiceQueryKeys.accounts.invoiceList(accountId)],
      });
    }

    paymentModalState.onClose();
  };

  const handleResetFilter = () => {
    onResetFilter();
    tableRef.current?.resetFilters();
  };

  return (
    <>
      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={invoices?.content ?? []}
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={invoices?.totalElements}
        totalPages={invoices?.totalPages}
        pager={pager}
        setPager={setPager}
        initialSortingState={[{ id: 'createDate', desc: true }]}
        initialFilters={filters?.filter(
          (filter) =>
            filter.operator !== FilterTypeOperator.TOGGLE &&
            filter.operator !== FilterTypeOperator.GLTE,
        )}
        onFiltersChange={(newColumnFilters) => {
          const externalFilters =
            filters?.filter(
              (filter) =>
                filter.operator === FilterTypeOperator.TOGGLE ||
                filter.operator === FilterTypeOperator.GLTE,
            ) ?? [];
          setFilters?.([...externalFilters, ...newColumnFilters]);
          setPager?.({ ...pager, page: 0, first: 0 });
        }}
        onRowClick={(row, _tableRow, openInNewWindow) => {
          const routeToNavigate = ROUTES.getInvoiceDetailRoute(row.id);
          navigateOrOpenNewWindow(navigate, routeToNavigate, openInNewWindow, {
            source,
          });
        }}
        isLoading={isLoading}
        emptyProps={{
          mainMessage: 'Looks like there are no invoices here.',
          smallMessage:
            'When you create a quote, the invoice will be added here.',
        }}
        emptyFilterResultsProps={{ btnLabel: 'Reset Filters' }}
        resetFilter={handleResetFilter}
        extraActiveFilters={extraActiveFilters}
      />
      {paymentModalState.open && !!invoice && (
        <PayInvoiceModal
          isOpen={paymentModalState.open}
          invoice={invoice}
          onClose={handlePaymentModalClose}
        />
      )}
      {!!invoice && revenueScheduleModalState.open && (
        <InvoiceRevenueScheduleModal
          isOpen
          invoice={invoice}
          onClose={() => revenueScheduleModalState.onClose()}
        />
      )}
      {enableInvoiceVoid && voidInvoiceModalState.open && !!invoice && (
        <VoidInvoiceModal
          isOpen
          invoice={invoice}
          onClose={voidInvoiceModalState.onClose}
          refreshData={refetch}
        />
      )}
    </>
  );
};
